import React, { useState } from 'react';
import { Cookie, Settings2, BarChart3, Megaphone, ToggleLeft, ToggleRight, Mail } from 'lucide-react';

const cookieTypes = [
  {
    id: "essential",
    icon: <Settings2 className="w-5 h-5 text-blue-600" />,
    title: "كوكيز أساسية",
    color: "bg-blue-50",
    description: "ضرورية لعمل الموقع بشكل صحيح، مثل تذكر بيانات حجز الموعد أثناء تعبئة النموذج. لا يمكن تعطيلها.",
    toggleable: false
  },
  {
    id: "performance",
    icon: <BarChart3 className="w-5 h-5 text-teal-600" />,
    title: "كوكيز الأداء والتحليلات",
    color: "bg-teal-50",
    description: "تساعدنا على فهم كيفية استخدام الزوار للموقع، مثل الصفحات الأكثر زيارة، لتحسين تجربة الاستخدام.",
    toggleable: true
  },
  {
    id: "marketing",
    icon: <Megaphone className="w-5 h-5 text-purple-600" />,
    title: "كوكيز تسويقية",
    color: "bg-purple-50",
    description: "تُستخدم لعرض محتوى وعروض تناسب اهتماماتك عند تفعيلها، ولا تُستخدم دون موافقتك.",
    toggleable: true
  }
];

const CookieToggle = ({ enabled, onToggle, disabled }) => {
  if (disabled) {
    return (
      <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
        مفعّلة دائمًا
      </span>
    );
  }
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-1 text-teal-600"
      aria-label="تبديل الكوكيز"
    >
      {enabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 text-gray-300" />}
    </button>
  );
};

const CookiePolicy = () => {
  const [preferences, setPreferences] = useState({
    performance: true,
    marketing: false
  });

  const togglePreference = (id) => {
    setPreferences((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section id="cookie-policy" className="py-24 bg-slate-50 font-sans" dir="rtl">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <Cookie className="w-4 h-4" />
            تصفح بثقة
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            سياسة الكوكيز
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            نستخدم ملفات تعريف الارتباط (الكوكيز) لتحسين تجربتك على موقعنا، توضح هذه الصفحة أنواعها وكيفية التحكم بها
          </p>
          <p className="text-sm text-gray-400 mt-4">آخر تحديث: يوليو 2026</p>
        </div>

        <div className="bg-white shadow-lg rounded-2xl p-8 border border-gray-100 mb-6">
          <h3 className="text-2xl font-bold text-blue-900 mb-2">ما هي الكوكيز؟</h3>
          <p className="text-gray-600 leading-relaxed">
            الكوكيز هي ملفات نصية صغيرة تُخزَّن على جهازك عند زيارة موقعنا، وتساعدنا على تذكر تفضيلاتك
            وتحسين أداء الموقع وفهم كيفية تفاعلك معه.
          </p>
        </div>

        <div className="space-y-6">
          {cookieTypes.map((cookie) => (
            <div
              key={cookie.id}
              id={cookie.id}
              className={`${cookie.color} p-6 rounded-2xl shadow-sm border border-gray-100`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm shrink-0">
                  {cookie.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {cookie.title}
                    </h3>
                    <CookieToggle
                      enabled={preferences[cookie.id]}
                      onToggle={() => togglePreference(cookie.id)}
                      disabled={!cookie.toggleable}
                    />
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {cookie.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <button className="flex-1 bg-gradient-to-l from-blue-700 to-teal-600 text-white font-bold py-3 px-6 rounded-full hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 shadow-md">
            حفظ التفضيلات
          </button>
          <button
            onClick={() => setPreferences({ performance: true, marketing: true })}
            className="flex-1 bg-white border border-gray-200 text-blue-900 font-bold py-3 px-6 rounded-full hover:shadow-md transition-all duration-300"
          >
            قبول الكل
          </button>
        </div>

        <div className="mt-8 bg-white shadow-lg rounded-2xl p-8 border border-gray-100 text-center">
          <div className="p-3 bg-teal-50 rounded-xl shadow-sm inline-flex mb-4">
            <Mail className="w-6 h-6 text-teal-600" />
          </div>
          <h3 className="text-xl font-bold text-blue-900 mb-2">أسئلة حول الكوكيز؟</h3>
          <p className="text-gray-600 mb-1">
            يمكنك دائمًا تعديل تفضيلاتك من هذه الصفحة، أو التواصل معنا عبر
          </p>
          <p className="text-blue-700 font-semibold">support@medicallab.com</p>
        </div>
      </div>
    </section>
  );
};

export default CookiePolicy;
